import * as Sentry from '@sentry/browser'
import React, { useState, useRef } from 'react'
import clsx from 'clsx'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Thumbnail } from 'components'
import Modal, { Confirmation } from 'components/Modal'
import { AuthService } from 'features/Auth'

import { IconArrowDown, IconProfile, IconLogout } from '../Icons'
import { routes } from 'helpers/routes'
import { useOnClickOutside } from 'helpers/hooks'
import { useResetRecoilState } from 'recoil'
import { userProfileState } from 'recoils/atoms'

import { MenuList } from 'types'

export interface ProfileProps extends IProfile {
  /**
   * Optional className
   */
  className?: string
}

export interface IProfile {
  profileName?: string | null
  profileImage?: string
}

export const Profile: React.FC<ProfileProps> = ({
  profileName = '',
  profileImage = '',
  className,
}: ProfileProps): JSX.Element => {
  const resetUserProfile = useResetRecoilState(userProfileState)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isModalLogOut, setIsModalLogOut] = useState<boolean>(false)

  const history = useHistory()
  const { t } = useTranslation('common')
  const divRef = useRef<HTMLDivElement>(null)

  const base = 'profile'
  const cn = clsx(base, className)

  const handleProfile = () => {
    history.push(routes.profile.edit.path)
  }

  const handleLogout = async () => {
    try {
      resetUserProfile()
      await AuthService.logout()
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const handleModalLogOut = () => {
    setIsModalLogOut(true)
  }

  const handleToggleDropdown = () => {
    if (!isModalLogOut) {
      setIsOpen(!isOpen)
    }
  }

  const renderModalLogOut = () => {
    return (
      isModalLogOut && (
        <Modal
          isIcon={false}
          isOpen={isModalLogOut}
          onClose={() => setIsModalLogOut(false)}
        >
          <Confirmation
            title={t('confirmation.logout.label')}
            description={t('confirmation.logout.description.label')}
            confirmText={t('confirmation.logout.submit.label')}
            cancelText={t('confirmation.logout.cancel.label')}
            onConfirm={() => handleLogout()}
            onClose={() => setIsModalLogOut(false)}
          />
        </Modal>
      )
    )
  }

  const options: MenuList[] = [
    {
      label: t('profile.label'),
      Icon: IconProfile,
      onClick: handleProfile,
    },
    {
      label: t('logout.label'),
      Icon: IconLogout,
      onClick: handleModalLogOut,
    },
  ]

  useOnClickOutside(divRef, () => setIsOpen(false))

  return (
    <div
      className={cn}
      tabIndex={0}
      onClick={handleToggleDropdown}
      data-testid='profile-container'
      ref={divRef}
    >
      <Thumbnail
        src={profileImage}
        className={`${base}__thumbnail`}
        variants='secondary'
        onError={resetUserProfile}
        data-testid='profile-thumbnail'
      />
      <span className={`${base}__name`} data-testid='profile-name'>
        {profileName}
      </span>
      <IconArrowDown
        className={clsx(`${base}__icon`, {
          'icon--rotate-arrow-up': isOpen,
        })}
        variants='xs'
      />
      {isOpen && (
        <div className={`${base}__dropdowns`} data-testid='profile-dropdown'>
          {options.map(({ label, Icon, onClick }, index) => (
            <div
              className={`${base}__dropdown`}
              onClick={onClick}
              key={`${label}${index}`}
              data-testid={`profile-option-${index}`}
            >
              {Icon && (
                <Icon
                  variants='sm'
                  data-testid={`profile-dropdown-icon-${index}`}
                />
              )}
              <span data-testid={`profile-dropdown-label-${index}`}>
                {label}
              </span>
            </div>
          ))}
        </div>
      )}
      {renderModalLogOut()}
    </div>
  )
}

export default Profile
